import * as Sentry from '@sentry/remix';
import SHA256 from 'crypto-js/sha256';

// Custom exception for SHA-256 hashing failures
export class Sha256HashError extends Error {}

/**
 * Creates a SHA-256 hash of a string input. Provides a privacy-safe way to hash
 * sensitive data without exposing the original content, using crypto-js for
 * consistent results across environments.
 * @param input - The string to hash
 * @returns SHA-256 hash as a hex string
 * @throws Sha256HashError if hashing fails
 */
export function hashSha256(input: string): string {
  if (!input || typeof input !== 'string') {
    throw new Sha256HashError('Invalid input: must be a non-empty string');
  }

  try {
    // Trim whitespace for consistent hashing
    const normalizedInput = input.trim();

    // Create SHA-256 hash using crypto-js
    const hash = SHA256(normalizedInput);

    // Convert to hex string
    return hash.toString();
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        input_length: input.length,
        input_preview: input.substring(0, 100),
      },
    });

    throw new Sha256HashError('Failed to hash input');
  }
}
